import React from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Truck, Wrench } from 'lucide-react';

const HospitalProduct = () => {
  const { t, lang } = useLanguage();

  const content = {
    en: {
      overline: "HOSPITAL USE PRODUCT",
      title: "How to Choose the Ideal Wheelchair",
      subtitle: "Quality, resistance, and competitive prices for your institution, clinic, or business.",
      features: [
        { icon: Wrench, label: "Ergonomic Design", desc: "Resistant and durable construction" },
        { icon: Truck, label: "Easy to Transport", desc: "Foldable for convenient storage" },
        { icon: ShieldCheck, label: "Mobility & Comfort", desc: "Independence in every journey" },
      ],
      cta: "Request a Quote",
      brand: "M-Pluss Medical",
    },
    es: {
      overline: "PRODUCTO DE USO HOSPITALARIO",
      title: "Como Elegir la Silla de Ruedas Ideal",
      subtitle: "Calidad, resistencia y precios competitivos para tu institucion, clinica o negocio.",
      features: [
        { icon: Wrench, label: "Diseno Ergonomico", desc: "Construccion resistente y duradera" },
        { icon: Truck, label: "Facil de Transportar", desc: "Plegable para almacenamiento conveniente" },
        { icon: ShieldCheck, label: "Movilidad y Confort", desc: "Independencia en cada trayecto" },
      ],
      cta: "Solicitar Cotizacion",
      brand: "M-Pluss Medical",
    },
  };

  const c = content[lang] || content.en;

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hospital-product" data-testid="hospital-product-section" className="py-0 bg-[#0A1628] relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 min-h-[600px]">
          {/* Left: Content */}
          <div className="flex flex-col justify-center px-8 md:px-16 py-20 relative z-10">
            <motion.p
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
              className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-6"
              data-testid="hospital-product-overline"
            >
              {c.overline}
            </motion.p>

            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-6"
              data-testid="hospital-product-title"
            >
              {c.title}
            </motion.h2>

            <motion.p
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="text-slate-300 text-base md:text-lg leading-relaxed mb-10 max-w-md"
            >
              {c.subtitle}
            </motion.p>

            <div className="space-y-5 mb-10">
              {c.features.map((feat, i) => {
                const Icon = feat.icon;
                return (
                  <motion.div
                    key={i}
                    variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 3}
                    className="flex items-center gap-4"
                    data-testid={`hospital-feature-${i}`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center border border-[#C5A55A]/40 flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#C5A55A]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{feat.label}</p>
                      <p className="text-slate-400 text-xs">{feat.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={6}
            >
              <button
                onClick={scrollToContact}
                className="group bg-[#C5A55A] text-[#0A1628] font-semibold px-8 py-4 uppercase tracking-wider transition-all duration-300 shadow-[0_4px_14px_0_rgba(197,165,90,0.39)] hover:shadow-[0_6px_20px_rgba(197,165,90,0.23)] hover:-translate-y-1 flex items-center gap-3"
                data-testid="hospital-product-cta"
              >
                {c.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>

          {/* Right: Image */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="relative flex items-center justify-center overflow-hidden min-h-[400px] lg:min-h-0"
          >
            {/* Overlays to blend image with dark bg and hide social frame */}
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0A1628] z-10 hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-[#0A1628]/80 z-10" />
            <div className="absolute inset-0 bg-[#0A1628]/20 z-[5]" />
            <img
              src="https://customer-assets.emergentagent.com/job_supplies-connect-1/artifacts/lwesd8op_image.png"
              alt="Hospital Wheelchair - M-Pluss Medical"
              className="w-full h-full object-cover object-center scale-110"
              data-testid="hospital-product-image"
              loading="lazy"
            />
            {/* Brand badge */}
            <div className="absolute bottom-8 right-8 z-20 bg-[#0A1628]/90 backdrop-blur-sm px-5 py-3 border border-[#C5A55A]/30">
              <p className="text-[#C5A55A] text-xs font-mono tracking-widest uppercase">{c.brand}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HospitalProduct;
